import { useState, useEffect, useCallback } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PricingAdjustmentWarning } from "@/components/PricingAdjustmentWarning";

// Color mapping for code types (matching RecentGuidesSwiper)
const CODE_TYPE_COLORS: Record<string, string> = {
  'CPT': '#60a5fa',
  'DRG': '#c084fc',
  'HCPCS': '#4ade80',
  'REV': '#facc15',
  'NDC': '#fb7185',
  'SUPPLY': '#fb923c',
};

interface ExtremePriceGougingItem {
  id: string;
  procedure_description: string;
  procedure_code: string;
  procedure_type: string;
  hospital_id: string;
  hospital_name: string;
  gross_markup_percentage: number;
  hospital_gross_charge: number;
  national_avg_gross: number;
  inpatient_outpatient: string;
  normalized_unit?: string;
  has_pricing_adjustments?: boolean;
  pricing_reliability_score?: number;
  adjustment_notes?: string;
  llm_quick_name?: string;
  llm_summary_description?: string;
}

const ITEMS_PER_PAGE = 30;
const PREVIEW_ITEMS = 10;

interface ExtremePriceGougingTableProps {
  showAll?: boolean;
  limit?: number;
}

export const ExtremePriceGougingTable = ({ showAll = false, limit = PREVIEW_ITEMS }: ExtremePriceGougingTableProps = {}) => {
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('CPT');

  // Helper function to format setting display with emoji
  const formatSettingWithEmoji = (setting: string) => {
    const normalized = setting?.toLowerCase() || '';
    if (normalized.includes('inpatient') && normalized.includes('outpatient')) return '🏥 Both';
    if (normalized.includes('both')) return '🏥 Both';
    if (normalized.includes('inpatient')) return '🏥 Inpatient';
    if (normalized.includes('outpatient')) return '🏥 Outpatient';
    return '❓ Unspecified';
  };

  // Helper function to get code page URL
  const getCodePageUrl = (procedureType: string, procedureCode: string) => {
    const type = procedureType?.toLowerCase();
    if (type === 'cpt') return `/cpt/${procedureCode}`;
    if (type === 'drg') return `/drg/${procedureCode}`;
    if (type === 'revenue') return `/rev/${procedureCode}`;
    if (type === 'hcpcs') return `/cpt/${procedureCode}`; // HCPCS uses CPT page
    if (type === 'drug' || type === 'ndc') return `/drug/${procedureCode}`;
    return `/cpt`;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useInfiniteQuery({
    queryKey: ['extreme-price-gouging-table', showAll, selectedFilter],
    queryFn: async ({ pageParam = 0 }) => {
      // For preview mode, only get the first set of results
      const effectiveLimit = showAll ? ITEMS_PER_PAGE : limit;
      const offset = showAll ? pageParam * ITEMS_PER_PAGE : 0;
      const endRange = showAll ? (pageParam + 1) * ITEMS_PER_PAGE - 1 : limit - 1;

      // Query from materialized view for fast cached results
      // The view is pre-filtered to gross_markup_percentage >= 500
      let baseQuery = supabase
        .from('mv_extreme_price_gouging')
        .select('*');

      // Apply procedure type filter with CPT code validation
      if (selectedFilter === 'CPT') {
        // For CPT filter: get only valid CPT codes
        // Valid CPT codes are 5 digits and don't contain dots (dots indicate revenue codes)
        // Exclude short codes by pattern matching - codes must be at least 5 characters
        baseQuery = baseQuery
          .eq('procedure_type', 'cpt')
          .not('procedure_code', 'like', '%.%')
          .not('procedure_code', 'like', '___')  // Not 3 chars
          .not('procedure_code', 'like', '____'); // Not 4 chars
      } else if (selectedFilter !== 'ALL') {
        // Other code types - no special filtering needed
        baseQuery = baseQuery.eq('procedure_type', selectedFilter.toLowerCase());
      } else {
        // For ALL: include all non-CPT codes, plus only valid CPT codes
        // This is complex, so we'll fetch all and filter client-side for ALL filter
        baseQuery = baseQuery;
      }

      const query = baseQuery.order('gross_markup_percentage', { ascending: false }).range(offset, endRange);

      const { data: analytics, error: analyticsError } = await query;

      if (analyticsError) throw analyticsError;
      if (!analytics || analytics.length === 0) return { items: [], nextPage: null };

      console.log(`[ExtremePriceGouging] Fetched ${analytics.length} records from cached view for filter: ${selectedFilter}`);

      // For ALL filter only, validate CPT codes client-side (since we can't filter at DB level for ALL)
      // For CPT filter, we already filtered at database level, so no need to filter again
      const validAnalytics = selectedFilter === 'ALL'
        ? analytics.filter(item => {
            if (item.procedure_type === 'cpt') {
              // Exclude CPT codes with dots or less than 5 digits
              if (item.procedure_code.includes('.')) return false;
              const numericCode = item.procedure_code.replace(/[^0-9]/g, '');
              return numericCode.length >= 5;
            }
            return true;
          })
        : analytics;

      console.log(`[ExtremePriceGouging] After validation: ${validAnalytics.length} valid records`);

      if (validAnalytics.length === 0) return { items: [], nextPage: null };

      // Hospital names are already included in the materialized view
      // No need for a separate join query

      // Get procedure codes to fetch consumer friendly names
      const cptCodes = validAnalytics.filter(a => a.procedure_type === 'cpt').map(a => a.procedure_code);
      const hcpcsCodes = validAnalytics.filter(a => a.procedure_type === 'hcpcs').map(a => a.procedure_code);
      const drgCodes = validAnalytics.filter(a => a.procedure_type === 'drg').map(a => a.procedure_code);

      console.log(`[ExtremePriceGouging] Filter: ${selectedFilter}, CPT: ${cptCodes.length}, HCPCS: ${hcpcsCodes.length}, DRG: ${drgCodes.length}`);

      // Fetch CPT and HCPCS consumer friendly names from procedures table
      let procedureInfo: Record<string, { quick_name: string; summary: string }> = {};

      const allProcedureCodes = [...cptCodes, ...hcpcsCodes];
      if (allProcedureCodes.length > 0) {
        const { data: procData } = await supabase
          .from('procedures')
          .select('code, llm_quick_name, llm_summary_description')
          .in('code', allProcedureCodes);

        if (procData) {
          console.log(`[ExtremePriceGouging] Fetched ${procData.length} procedure records with LLM data`);
          procData.forEach(proc => {
            procedureInfo[proc.code] = {
              quick_name: proc.llm_quick_name || '',
              summary: proc.llm_summary_description || ''
            };
          });
        }
      }

      // Fetch DRG consumer friendly names from drg_codes table
      let drgInfo: Record<string, { quick_name: string; summary: string }> = {};
      if (drgCodes.length > 0) {
        // Extract numeric codes from DRG codes (e.g., "MS-003" -> "003")
        const numericDrgCodes = drgCodes.map(code => {
          const match = code.match(/\d+/);
          return match ? match[0].padStart(3, '0') : code;
        });

        const { data: drgData } = await supabase
          .from('drg_codes')
          .select('drg_code, llm_quick_name, llm_summary_description')
          .in('drg_code', numericDrgCodes);

        if (drgData) {
          console.log(`[ExtremePriceGouging] Fetched ${drgData.length} DRG records with LLM data`);
          drgData.forEach(drg => {
            // Map back to the original DRG code format
            const originalCode = drgCodes.find(code => {
              const match = code.match(/\d+/);
              return match && match[0].padStart(3, '0') === drg.drg_code;
            });
            if (originalCode) {
              drgInfo[originalCode] = {
                quick_name: drg.llm_quick_name || '',
                summary: drg.llm_summary_description || ''
              };
            }
          });
        }
      }

      // Merge the data - hospital_name is already in the materialized view
      const items = validAnalytics.map(analytic => {
        const info = (analytic.procedure_type === 'cpt' || analytic.procedure_type === 'hcpcs')
          ? procedureInfo[analytic.procedure_code]
          : analytic.procedure_type === 'drg'
          ? drgInfo[analytic.procedure_code]
          : null;

        return {
          ...analytic,
          llm_quick_name: info?.quick_name || '',
          llm_summary_description: info?.summary || ''
        };
      });

      const itemsWithLLM = items.filter(i => i.llm_quick_name).length;
      console.log(`[ExtremePriceGouging] Total items: ${items.length}, Items with LLM names: ${itemsWithLLM}`);

      return {
        items,
        nextPage: showAll && validAnalytics.length === ITEMS_PER_PAGE ? pageParam + 1 : null
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    enabled: true, // Always enabled, but behavior changes based on showAll
  });

  const handleScroll = useCallback(() => {
    if (!showAll || !hasNextPage || isFetchingNextPage) return;

    // Use window scroll for full-page infinite scroll
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollThreshold = 0.8; // Trigger when 80% scrolled

    if (scrollY + windowHeight >= documentHeight * scrollThreshold) {
      fetchNextPage();
    }
  }, [showAll, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!showAll) return;

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showAll, handleScroll]);

  // Define filter buttons with colors matching Recent Educational Guides
  const filterButtons = [
    { label: 'ALL', value: 'ALL', color: '#9ca3af' },
    { label: 'CPT', value: 'CPT', color: '#60a5fa' },
    { label: 'DRG', value: 'DRG', color: '#c084fc' },
    { label: 'HCPCS', value: 'HCPCS', color: '#4ade80' },
    { label: 'REV', value: 'REV', color: '#facc15' },
    { label: 'NDC', value: 'NDC', color: '#fb7185' },
    { label: 'SUPPLY', value: 'SUPPLY', color: '#fb923c' },
  ];

  if (isLoading) {
    return (
      <div className="w-full flex flex-col items-center">
        <div className="w-full">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blood-red mb-4 text-center">
            Most Extreme Price Gouging Detected
          </h3>
          <Card className="bg-black/90 border-destructive/30">
            <CardContent className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-destructive" />
              <p className="text-muted-foreground">Loading extreme price gouging data...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex flex-col items-center">
        <div className="w-full">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blood-red mb-4 text-center">
            Most Extreme Price Gouging Detected
          </h3>
          <Card className="bg-destructive/10 border-destructive/30">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-destructive" />
              <p className="text-destructive">Failed to load price gouging data</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const allItems = data?.pages.flatMap(page => page.items) || [];

  if (allItems.length === 0) {
    return (
      <div className="w-full flex flex-col items-center">
        <div className="w-full">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blood-red mb-4 text-center">
            Most Extreme Price Gouging Detected
          </h3>

          {/* Filter Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            {filterButtons.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedFilter(filter.value)}
                className="px-4 py-2 rounded font-mono text-sm font-bold transition-all hover:opacity-80"
                style={{
                  backgroundColor: selectedFilter === filter.value ? filter.color : '#000000a1',
                  color: selectedFilter === filter.value ? '#000000' : filter.color,
                  borderColor: filter.color,
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  boxShadow: selectedFilter === filter.value ? `0 0 12px ${filter.color}40` : 'none'
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <Card className="bg-muted/10 border-muted/30">
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">No extreme price gouging data available for {filterButtons.find(f => f.value === selectedFilter)?.label} codes</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const getMarkupColor = (markup: number) => {
    if (markup >= 2000) return "text-red-400";
    if (markup >= 1000) return "text-red-500";
    if (markup >= 500) return "text-orange-400";
    return "text-yellow-400";
  };

  const getMarkupSeverity = (markup: number) => {
    if (markup >= 2000) return "EXTREME";
    if (markup >= 1000) return "SEVERE";
    if (markup >= 500) return "HIGH";
    return "MODERATE";
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full">
        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blood-red mb-4 text-center">
          Most Extreme Price Gouging Detected
        </h3>

        {/* Filter Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
          {filterButtons.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setSelectedFilter(filter.value)}
              className="px-4 py-2 rounded font-mono text-sm font-bold transition-all hover:opacity-80"
              style={{
                backgroundColor: selectedFilter === filter.value ? filter.color : '#000000a1',
                color: selectedFilter === filter.value ? '#000000' : filter.color,
                borderColor: filter.color,
                borderWidth: '2px',
                borderStyle: 'solid',
                boxShadow: selectedFilter === filter.value ? `0 0 12px ${filter.color}40` : 'none'
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <Card className="bg-black/90 border-destructive/30">
        {showAll ? (
          // Full page table without scroll container
          <div>
            <Table>
              <TableHeader className="sticky top-0 bg-black/80 backdrop-blur z-10">
                <TableRow className="border-destructive/30">
                  <TableHead className="text-destructive font-semibold">Rank</TableHead>
                  <TableHead className="text-destructive font-semibold">Procedure</TableHead>
                  <TableHead className="text-destructive font-semibold">Hospital</TableHead>
                  <TableHead className="text-destructive font-semibold">Code</TableHead>
                  <TableHead className="text-destructive font-semibold">Setting</TableHead>
                  <TableHead className="text-destructive font-semibold text-right">Hospital Price</TableHead>
                  <TableHead className="text-destructive font-semibold text-right">National Avg</TableHead>
                  <TableHead className="text-destructive font-semibold">Severity</TableHead>
                  <TableHead className="text-destructive font-semibold text-center">Compare Prices</TableHead>
                  <TableHead className="text-destructive font-semibold text-right">Markup</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allItems.map((item, index) => (
                  <TableRow
                    key={`${item.id}-${item.procedure_code}-${item.hospital_id}-${index}`}
                    className="border-destructive/20 hover:bg-destructive/5 transition-colors"
                  >
                    <TableCell className="font-mono text-sm">
                      #{index + 1}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {item.llm_quick_name ? (
                        <>
                          <div className="font-medium text-foreground truncate" title={item.llm_quick_name}>
                            {item.llm_quick_name}
                          </div>
                          {item.llm_summary_description && (
                            <div className="text-xs text-muted-foreground truncate mt-0.5" title={item.llm_summary_description}>
                              {item.llm_summary_description}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground/60 truncate mt-0.5" title={item.procedure_description}>
                            {item.procedure_description}
                          </div>
                        </>
                      ) : (
                        <div className="font-medium text-foreground truncate" title={item.procedure_description}>
                          {item.procedure_description}
                        </div>
                      )}
                      <PricingAdjustmentWarning
                        hasPricingAdjustments={item.has_pricing_adjustments || false}
                        adjustmentNotes={item.adjustment_notes}
                        pricingReliabilityScore={item.pricing_reliability_score}
                        variant="inline"
                        className="mt-1"
                      />
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <Link 
                        to={`/hospital/${item.hospital_id}`}
                        className="text-blue-400 hover:text-blue-300 underline truncate block"
                        title={item.hospital_name}
                      >
                        {item.hospital_name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div
                        className="px-2 py-1 rounded border-2 font-mono text-sm font-bold w-fit"
                        style={{
                          borderColor: CODE_TYPE_COLORS[item.procedure_type?.toUpperCase()] || '#ffffff',
                          color: CODE_TYPE_COLORS[item.procedure_type?.toUpperCase()] || '#ffffff',
                          backgroundColor: '#000000'
                        }}
                      >
                        {item.procedure_code}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className="px-3 py-1.5 rounded border-2 border-white text-xs font-semibold w-fit"
                        style={{ backgroundColor: '#000000' }}
                        title={item.inpatient_outpatient || 'Unknown'}
                      >
                        {formatSettingWithEmoji(item.inpatient_outpatient || 'Unknown')}
                        {item.normalized_unit && (
                          <span className="ml-1 text-muted-foreground">- {item.normalized_unit}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-mono text-lg font-bold text-destructive">
                        ${Number(item.hospital_gross_charge)?.toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-mono text-sm text-muted-foreground">
                        ${item.national_avg_gross?.toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="destructive"
                        className={`font-bold`}
                      >
                        {getMarkupSeverity(item.gross_markup_percentage)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                        className="text-xs"
                      >
                        <Link to={getCodePageUrl(item.procedure_type, item.procedure_code)}>
                          📊 Compare All Prices
                        </Link>
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`font-bold text-lg ${getMarkupColor(item.gross_markup_percentage)}`}>
                        +{Number(item.gross_markup_percentage).toLocaleString(undefined, { maximumFractionDigits: 0 })}%
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextPage && (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-destructive" />
                <p className="text-lg text-muted-foreground">Loading more extreme cases...</p>
              </div>
            )}
            
            {!hasNextPage && allItems.length > 0 && (
              <div className="p-8 text-center">
                <p className="text-lg text-muted-foreground">
                  Showing all {allItems.length} extreme price gouging cases
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  These represent the worst pricing violations in the healthcare system
                </p>
              </div>
            )}
          </div>
        ) : (
          // Preview mode - no scroll area, fixed height
          <div>
            <Table>
              <TableHeader>
                <TableRow className="border-destructive/30">
                  <TableHead className="text-destructive font-semibold">Rank</TableHead>
                  <TableHead className="text-destructive font-semibold">Procedure</TableHead>
                  <TableHead className="text-destructive font-semibold">Hospital</TableHead>
                  <TableHead className="text-destructive font-semibold">Code</TableHead>
                  <TableHead className="text-destructive font-semibold">Setting</TableHead>
                  <TableHead className="text-destructive font-semibold text-right">Hospital Price</TableHead>
                  <TableHead className="text-destructive font-semibold text-right">National Avg</TableHead>
                  <TableHead className="text-destructive font-semibold text-center">Compare Prices</TableHead>
                  <TableHead className="text-destructive font-semibold text-right">Markup</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allItems.slice(0, limit).map((item, index) => (
                  <TableRow
                    key={`${item.id}-${item.procedure_code}-${item.hospital_id}-${index}`}
                    className="border-destructive/20 hover:bg-destructive/5 transition-colors"
                  >
                    <TableCell className="font-mono text-sm">
                      #{index + 1}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {item.llm_quick_name ? (
                        <>
                          <div className="font-medium text-foreground truncate" title={item.llm_quick_name}>
                            {item.llm_quick_name}
                          </div>
                          {item.llm_summary_description && (
                            <div className="text-xs text-muted-foreground truncate mt-0.5" title={item.llm_summary_description}>
                              {item.llm_summary_description}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground/60 truncate mt-0.5" title={item.procedure_description}>
                            {item.procedure_description}
                          </div>
                        </>
                      ) : (
                        <div className="font-medium text-foreground truncate" title={item.procedure_description}>
                          {item.procedure_description}
                        </div>
                      )}
                      <PricingAdjustmentWarning
                        hasPricingAdjustments={item.has_pricing_adjustments || false}
                        adjustmentNotes={item.adjustment_notes}
                        pricingReliabilityScore={item.pricing_reliability_score}
                        variant="inline"
                        className="mt-1"
                      />
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <Link 
                        to={`/hospital/${item.hospital_id}`}
                        className="text-blue-400 hover:text-blue-300 underline truncate block"
                        title={item.hospital_name}
                      >
                        {item.hospital_name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div
                        className="px-2 py-1 rounded border-2 font-mono text-sm font-bold w-fit"
                        style={{
                          borderColor: CODE_TYPE_COLORS[item.procedure_type?.toUpperCase()] || '#ffffff',
                          color: CODE_TYPE_COLORS[item.procedure_type?.toUpperCase()] || '#ffffff',
                          backgroundColor: '#000000'
                        }}
                      >
                        {item.procedure_code}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className="px-3 py-1.5 rounded border-2 border-white text-xs font-semibold w-fit"
                        style={{ backgroundColor: '#000000' }}
                        title={item.inpatient_outpatient || 'Unknown'}
                      >
                        {formatSettingWithEmoji(item.inpatient_outpatient || 'Unknown')}
                        {item.normalized_unit && (
                          <span className="ml-1 text-muted-foreground">- {item.normalized_unit}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-mono text-lg font-bold text-destructive">
                        ${Number(item.hospital_gross_charge)?.toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-mono text-sm text-muted-foreground">
                        ${item.national_avg_gross?.toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                        className="text-xs"
                      >
                        <Link to={getCodePageUrl(item.procedure_type, item.procedure_code)}>
                          📊 Compare All Prices
                        </Link>
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`font-bold text-lg ${getMarkupColor(item.gross_markup_percentage)}`}>
                        +{Number(item.gross_markup_percentage).toLocaleString(undefined, { maximumFractionDigits: 0 })}%
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* See All button for preview mode */}
            <div className="p-6 text-center border-t border-destructive/30">
              <Button asChild className="bg-destructive hover:bg-destructive/90">
                <Link to="/extreme-price-gouging">
                  See All Extreme Cases
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        )}
      </Card>
      </div>
    </div>
  );
};